import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Key, Shield, User, Bell, ChevronRight, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import VerificationModal from '../../components/VerificationModal';
import { apiErrorMessage } from '../../lib/utils';
import { logger } from '../../lib/logger';

const UserSettings = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [verificationModal, setVerificationModal] = useState({ isOpen: false, type: null });
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters with letters and numbers');
      return;
    }

    setLoading(true);
    try {
      await authAPI.changePassword({
        old_password: passwordData.old_password,
        new_password: passwordData.new_password,
      });
      toast.success('Password changed successfully');
      setPasswordData({
        old_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to change password'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = async () => {
    // Refresh user data
    try {
      await refreshUser();
    } catch (error) {
      logger.error('Failed to refresh user data:', error);
    }
  };

  return (
    <div data-testid="user-settings-page">
      <header className="page-header glass-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-[Manrope]">My Settings</h1>
            <p className="text-slate-500">Manage your account settings</p>
          </div>
        </div>
      </header>

      <div className="page-content max-w-2xl">
        <div className="space-y-6">
          {/* Profile Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <User className="w-5 h-5 text-red-500" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Name:</span>
                <span className="text-sm font-medium">{user?.full_name}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-500">Email:</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{user?.email}</span>
                  {user?.email_verified ? (
                    <Badge className="bg-green-100 text-green-800 border-green-200">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Verified
                    </Badge>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                        <XCircle className="w-3 h-3 mr-1" />
                        Unverified
                      </Badge>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setVerificationModal({ isOpen: true, type: 'email' })}
                        className="h-7 text-xs"
                      >
                        Verify
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              {user?.phone && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-500">Phone:</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{user.phone}</span>
                    {user?.phone_verified ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                          <XCircle className="w-3 h-3 mr-1" />
                          Unverified
                        </Badge>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setVerificationModal({ isOpen: true, type: 'phone' })}
                          className="h-7 text-xs"
                        >
                          Verify
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm text-slate-500">Role:</span>
                <span className="text-sm font-medium capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
              {user?.department && (
                <div className="flex justify-between">
                  <span className="text-sm text-slate-500">Department:</span>
                  <span className="text-sm font-medium">{user.department}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Notification Preferences Link */}
          <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/settings/notifications')}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-slate-900">Notification Preferences</p>
                    <p className="text-sm text-slate-500">Manage email and SMS notifications</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </div>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
                <Key className="w-5 h-5 text-red-500" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="old_password">Current Password *</Label>
                  <Input
                    id="old_password"
                    type="password"
                    value={passwordData.old_password}
                    onChange={(e) => setPasswordData({...passwordData, old_password: e.target.value})}
                    placeholder="Enter current password"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="new_password">New Password *</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={passwordData.new_password}
                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                    placeholder="Enter new password (min 8 characters)"
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm New Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={passwordData.confirm_password}
                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                    placeholder="Re-enter new password"
                    required
                    minLength={8}
                  />
                </div>

                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800">
                    <Shield className="w-4 h-4 inline mr-1" />
                    Password must be at least 8 characters with letters and numbers long
                  </p>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    type="submit"
                    className="bg-red-500 hover:bg-red-600"
                    disabled={loading}
                  >
                    <Key className="w-4 h-4 mr-2" />
                    {loading ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Verification Modal */}
      <VerificationModal
        isOpen={verificationModal.isOpen}
        onClose={() => setVerificationModal({ isOpen: false, type: null })}
        type={verificationModal.type}
        onSuccess={handleVerificationSuccess}
      />
    </div>
  );
};

export default UserSettings;
